// All-requests table — links to /customer/requests/:id detail pages.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Filter as Plus, Search } from 'lucide-react';
import { PortalShell } from '../../components/shared/PortalShell';
import { StatusPill } from '../../components/shared/primitives';
import { DataTable, type Column } from '../../components/shared/DataTable';
import { CUSTOMER_NAV } from '../_navs';
import { useCustomerNav } from '../../lib/pendingAction';
import { requests, type RequestStatus, type ServiceRequestWithItems } from '../../lib/api';
import { formatHtgMinor } from '../../lib/money';

const STATUS_FILTERS: Array<{ value: 'all' | RequestStatus; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'quote_pending', label: 'Quote pending' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'paid', label: 'Paid' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function CustomerRequestsPage() {
  const q = useQuery({ queryKey: ['me', 'requests'], queryFn: () => requests.list() });
  const [statusFilter, setStatusFilter] = useState<'all' | RequestStatus>('all');
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    let list = q.data ?? [];
    if (statusFilter !== 'all') list = list.filter((r) => r.status === statusFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((r) =>
        String(r.id).includes(s) ||
        (r.recipient?.full_name ?? '').toLowerCase().includes(s) ||
        (r.title ?? '').toLowerCase().includes(s) ||
        (r.category?.label ?? '').toLowerCase().includes(s)
      );
    }
    return [...list].sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''));
  }, [q.data, statusFilter, search]);

  // Home carries a badge when this role owes an action, so it stays
  // visible from any screen.
  const nav = useCustomerNav(CUSTOMER_NAV);

  const columns: Column<ServiceRequestWithItems>[] = [
    {
      key: 'ref', header: 'Request', type: 'mono', width: '110px',
      value: (r) => `REQ-${String(r.id).padStart(4, '0')}` },
    {
      key: 'what', header: 'Details', type: 'strong', width: 'minmax(0,1.5fr)',
      value: (r) => r.title || r.category?.label || `Cat ${r.category_id}` },
    {
      key: 'for', header: 'For', type: 'text', width: 'minmax(0,1fr)',
      value: (r) => r.recipient?.full_name?.split(' ')[0] ?? '—' },
    {
      key: 'created', header: 'Created', type: 'muted', width: '110px',
      value: (r) => (r.created_at ? new Date(r.created_at).toLocaleDateString() : '—') },
    {
      key: 'items', header: 'Items', type: 'money', width: '70px',
      value: (r) => r.cart_item_count },
    {
      key: 'total', header: 'Cart', type: 'money', width: '120px',
      // A draft has no total worth showing — it has an unfinished cart. Say
      // what to do with it instead.
      value: (r) => (r.status === 'draft'
        ? <span className="font-semibold text-brand-600">Continue →</span>
        : r.cart_subtotal_htg_minor > 0 ? formatHtgMinor(r.cart_subtotal_htg_minor) : '—') },
    {
      key: 'status', header: 'Status', type: 'node', width: '130px',
      value: (r) => <StatusPill status={r.status} /> },
  ];

  return (
    <PortalShell
      nav={nav}
      activeId="requests"
      title="Requests"
      subtitle="Everything you've sent on behalf of family."
      actions={
        <Link to="/customer/requests/new" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-600 text-white text-[12px] font-semibold hover:bg-brand-700">
          <Plus className="w-3.5 h-3.5" /> New request
        </Link>
      }
    >
      <div className="space-y-4">
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted)]" aria-hidden="true" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by id, recipient, category, title…"
            aria-label="Search requests"
            className="w-full rounded-lg border border-[var(--color-border)] bg-white py-2 pl-9 pr-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>

        <DataTable
          title="Your requests"
          subtitle="Newest first."
          filters={STATUS_FILTERS.map((f) => ({ key: f.value, label: f.label }))}
          activeFilter={statusFilter}
          onFilter={(k) => setStatusFilter(k as 'all' | RequestStatus)}
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          rowHref={hrefFor}
          loading={q.isLoading}
          empty={
            statusFilter !== 'all' || search.trim()
              ? 'Nothing matches this filter.'
              : 'No requests yet. Build a cart for a family member to get started.'
          }
          renderCard={(r) => <RequestCard r={r} />}
        />
      </div>
    </PortalShell>
  );
}

// Drafts link back into the builder to finish; everything else opens detail.
function hrefFor(r: ServiceRequestWithItems) {
  return r.status === 'draft' ? `/customer/requests/new?draft=${r.id}` : `/customer/requests/${r.id}`;
}

function RequestCard({ r }: { r: ServiceRequestWithItems }) {
  const href = hrefFor(r);
  return (
    <Link to={href} className="block px-4 py-3 active:bg-[var(--color-panel)]">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[12px] text-[#063B9E]">REQ-{String(r.id).padStart(4, '0')}</span>
        <StatusPill status={r.status} />
      </div>
      <p className="mt-1 text-[13.5px] font-semibold text-[var(--color-ink)] truncate">{r.title || r.category?.label || `Cat ${r.category_id}`}</p>
      <div className="mt-1 flex items-center justify-between text-[12px] text-[var(--color-secondary-2)]">
        <span>For {r.recipient?.full_name?.split(' ')[0] ?? '—'} · {r.cart_item_count > 0 ? `${r.cart_item_count} items` : '—'}</span>
        {r.status === 'draft'
          ? <span className="font-semibold text-brand-600">Continue →</span>
          : <span className="font-mono text-[var(--color-ink)]">{r.cart_subtotal_htg_minor > 0 ? formatHtgMinor(r.cart_subtotal_htg_minor) : '—'}</span>}
      </div>
    </Link>
  );
}

